import React, { useEffect, useRef } from 'react';
import { useStrategyInsights } from '../../context/StrategyInsightsContext';
import { useNavigate } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabase';
import { toast } from 'sonner';
import StrategyGeneratorModal from '../planning/tools/StrategyGeneratorModal';
import { useWorkspace } from '../../context/WorkspaceContext';

const InsightCard = ({ insight, dismissInsight, navigate, openGenerator }) => {
  const handleAction = async (action) => {
    // Intercept specific labels to override default edge function behavior
    if (action.label === 'Complete Comprehensive Strategy' || action.label === 'Start Comprehensive Strategy') {
      return openGenerator(insight.workspace_id);
    }
    if (action.label === 'View Audit') {
      return navigate('/strategy');
    }

    if (action.action === 'navigate') {
      navigate(action.url);
    } else if (action.action === 'dismiss') {
      dismissInsight(insight.id, insight.type, insight.reference_id, action.dismiss_duration);
    } else if (action.action === 'complete_task') {
      try {
        await supabase.from('scheduled_tasks').update({ status: 'completed' }).eq('id', action.task_id);
        toast.success("Task completed");
        dismissInsight(insight.id, insight.type, insight.reference_id, "completed");
      } catch (e) {
        toast.error("Failed to complete task");
      }
    } else if (action.action === 'delete_task') {
      try {
        await supabase.from('scheduled_tasks').delete().eq('id', action.task_id);
        toast.success("Task deleted");
        dismissInsight(insight.id, insight.type, insight.reference_id, "deleted");
      } catch (e) {
        toast.error("Failed to delete task");
      }
    } else if (action.action === 'open_generator') {
      openGenerator(insight.workspace_id);
    }
  };

  return (
    <div className="bg-white dark:bg-charcoal-800 border border-charcoal-200 dark:border-charcoal-700 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0 mt-0.5">{insight.icon}</span>
        <div className="flex-1">
          <h4 className="font-semibold text-charcoal-900 dark:text-white text-sm mb-1">{insight.title}</h4>
          <p className="text-charcoal-600 dark:text-charcoal-300 text-sm mb-3">{insight.description}</p>

          <div className="flex flex-wrap gap-2">
            {insight.actions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleAction(action)}
                className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                  action.type === 'primary'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : action.type === 'secondary'
                    ? 'bg-charcoal-100 dark:bg-charcoal-700 hover:bg-charcoal-200 dark:hover:bg-charcoal-600 text-charcoal-900 dark:text-white'
                    : action.type === 'danger'
                    ? 'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:underline px-1'
                    : 'text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200 hover:underline px-1'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StrategyDashboardPanel = ({ isOpen, onClose }) => {
  const { insights, loading, dismissInsight } = useStrategyInsights();
  const { switchWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const groupedInsights = {
    HIGH_PRIORITY: insights.filter(i => i.category === 'HIGH_PRIORITY'),
    RECOMMENDED: insights.filter(i => i.category === 'RECOMMENDED'),
    UPCOMING: insights.filter(i => i.category === 'UPCOMING'),
  };

  const handleOpenGenerator = (workspaceId) => {
    if (workspaceId) {
      switchWorkspace(workspaceId);
    }
    onClose();
    navigate('/strategy');
  };

  return (
    <>
      <div className="fixed inset-0 bg-charcoal-900/50 backdrop-blur-sm z-[9999] transition-opacity" aria-hidden="true" onClick={onClose} />

      <div
        ref={panelRef}
        className="fixed inset-y-0 right-0 z-[9999] w-full max-w-md bg-charcoal-50 dark:bg-charcoal-900 shadow-2xl border-l border-charcoal-200 dark:border-charcoal-800 flex flex-col transform transition-transform duration-300 ease-in-out md:translate-x-0 bottom-0 md:top-0 h-[85vh] md:h-full rounded-t-2xl md:rounded-none mt-auto md:mt-0"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-charcoal-200 dark:border-charcoal-800 bg-white dark:bg-charcoal-950 rounded-t-2xl md:rounded-none">
          <div className="flex items-center gap-2">
            <span className="text-xl">🌱</span>
            <h2 className="text-lg font-bold text-charcoal-900 dark:text-white">Strategy Dashboard</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-charcoal-100 dark:hover:bg-charcoal-800 text-charcoal-500 dark:text-charcoal-400 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : insights.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
              <span className="text-5xl mb-2">🌱</span>
              <h3 className="text-xl font-bold text-charcoal-900 dark:text-white">All Caught Up!</h3>
              <p className="text-charcoal-600 dark:text-charcoal-400 max-w-[250px]">
                Your strategies are healthy and up to date.
              </p>
              <div className="mt-6 flex flex-col gap-3 w-full">
                <button onClick={() => { onClose(); navigate('/strategy'); }} className="w-full py-2 px-4 border border-charcoal-300 dark:border-charcoal-700 hover:bg-charcoal-100 dark:hover:bg-charcoal-800 text-charcoal-700 dark:text-charcoal-300 rounded-lg font-medium transition-colors">
                  Create New Strategy
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {groupedInsights.HIGH_PRIORITY.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-red-500 uppercase tracking-wider mb-4 border-b border-red-200 dark:border-red-900/50 pb-2">
                    High Priority
                  </h3>
                  {groupedInsights.HIGH_PRIORITY.map(insight => (
                    <InsightCard key={insight.id} insight={insight} dismissInsight={dismissInsight} navigate={(url) => { navigate(url); onClose(); }} openGenerator={handleOpenGenerator} />
                  ))}
                </div>
              )}

              {groupedInsights.RECOMMENDED.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-4 border-b border-blue-200 dark:border-blue-900/50 pb-2">
                    Recommended
                  </h3>
                  {groupedInsights.RECOMMENDED.map(insight => (
                    <InsightCard key={insight.id} insight={insight} dismissInsight={dismissInsight} navigate={(url) => { navigate(url); onClose(); }} openGenerator={handleOpenGenerator} />
                  ))}
                </div>
              )}

              {groupedInsights.UPCOMING.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider mb-4 border-b border-charcoal-200 dark:border-charcoal-800 pb-2">
                    Upcoming
                  </h3>
                  {groupedInsights.UPCOMING.map(insight => (
                    <InsightCard key={insight.id} insight={insight} dismissInsight={dismissInsight} navigate={(url) => { navigate(url); onClose(); }} openGenerator={handleOpenGenerator} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StrategyDashboardPanel;
